import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

export interface ScanCtaAction {
  label: string;
  onClick: () => void;
  /** Stable hook for component tests. */
  testId?: string;
}

interface ScanCtaBarProps {
  /** The current target, so the finishing action is never ambiguous. */
  caption: string;
  primary?: ScanCtaAction;
  secondary?: ScanCtaAction;
}

/**
 * The finishing action bar (scanner-surface 05). It sticks to the bottom of the
 * main scroll area while the level-2 preview scrolls, is full-bleed against the
 * shell's content padding (`px: xs 2 / md 3 / lg 4`) with square corners, and
 * therefore sits above the phone footer.
 */
export function ScanCtaBar({ caption, primary, secondary }: ScanCtaBarProps) {
  if (!caption && !primary && !secondary) return null;

  return (
    <Box
      data-testid="scan-cta-bar"
      sx={(theme) => ({
        position: "sticky",
        bottom: 0,
        zIndex: 3,
        mt: 1.5,
        // Full-bleed: cancel the shell's content padding. The base value is the
        // xs padding; md/lg widen it with the shell (px: xs 2 / md 3 / lg 4).
        mx: -2,
        px: 2,
        [theme.breakpoints.up("md")]: { mx: -3, px: 3 },
        [theme.breakpoints.up("lg")]: { mx: -4, px: 4 },
        pt: 1,
        pb: 1,
        bgcolor: "background.paper",
        borderTop: 1,
        borderColor: "divider",
        borderRadius: 0,
        boxShadow: "0 -8px 18px rgba(0,0,0,0.14)",
      })}
    >
      {caption && (
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{ display: "block", mb: primary || secondary ? 0.5 : 0 }}
        >
          {caption}
        </Typography>
      )}
      {(primary || secondary) && (
        <Stack direction="row" spacing={1}>
          {primary && (
            <Button
              fullWidth
              variant="contained"
              onClick={primary.onClick}
              data-testid={primary.testId}
            >
              {primary.label}
            </Button>
          )}
          {secondary && (
            <Button
              fullWidth
              variant="outlined"
              onClick={secondary.onClick}
              data-testid={secondary.testId}
            >
              {secondary.label}
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
}
